import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  authApi,
  clearAuthStorage,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '@/api/client';
import type { User } from '@/types';

/**
 * Set SSO cookie on .aikka.ai domain (frontend fallback).
 * The backend also sets httpOnly cookies, but this JS-accessible cookie
 * ensures product iframes can detect auth state immediately.
 */
function setSsoCookie(token: string | null) {
  const domain = window.location.hostname.includes('aikka.ai') ? '.aikka.ai' : window.location.hostname;
  if (token) {
    document.cookie = `aikka_token=${token}; domain=${domain}; path=/; max-age=${15 * 60}; secure; samesite=lax`;
  } else {
    document.cookie = `aikka_token=; domain=${domain}; path=/; max-age=0; secure; samesite=lax`;
  }
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  companyName?: string;
  captchaToken?: string;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  /** Establish a session from tokens obtained via an OAuth callback. */
  setSession: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => void;
  hasProductAccess: (slug: string) => boolean;
  getProductRole: (slug: string) => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

type NormalizedProductAccess = { slug: string; role?: string };

function normalizeProductAccess(user: User | null): NormalizedProductAccess[] {
  if (!user) return [];

  if (Array.isArray(user.products)) {
    return user.products;
  }

  const roles = (user as User & {
    productRoles?: Array<{ product?: { slug?: string }; role?: string }>;
  }).productRoles;

  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .map((entry) => ({ slug: entry.product?.slug ?? '', role: entry.role }))
    .filter((entry) => !!entry.slug);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data: user = null, isLoading } = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async (): Promise<User | null> => {
      try {
        const { data } = await authApi.me();
        return data as User;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const login = useCallback(
    async (email: string, password: string) => {
      const { data } = await authApi.login(email, password);
      setAuthToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setSsoCookie(data.accessToken);
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
    [queryClient],
  );

  const register = useCallback(
    async ({ email, password, name, companyName, captchaToken }: RegisterData) => {
      const { data } = await authApi.register({
        email,
        password,
        name,
        accountName: companyName,
        captchaToken,
      });
      setAuthToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setSsoCookie(data.accessToken);
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
    [queryClient],
  );

  const setSession = useCallback(
    async (accessToken: string, refreshToken: string) => {
      setAuthToken(accessToken);
      setRefreshToken(refreshToken);
      setSsoCookie(accessToken);
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    /*
     * LA MOITIÉ SERVEUR MANQUAIT, et c'est elle qui rallumait la session.
     *
     * Ne vider que le local laissait intacts les cookies httpOnly posés par le
     * back ; `JwtStrategy` lit le cookie d'accès en repli de l'en-tête, donc le
     * `GET /auth/me` du chargement suivant repartait authentifié et l'opérateur
     * se retrouvait reconnecté en rafraîchissant la page. Sur un poste partagé,
     * le suivant entrait dans le compte du précédent.
     *
     * Le jeton est lu AVANT le vidage — après, il n'y a plus rien à révoquer.
     */
    const refreshToken = getRefreshToken();

    /*
     * On n'attend pas, et on n'échoue pas. L'écran doit basculer sur-le-champ, et
     * un réseau coupé ne doit jamais retenir quelqu'un dans un compte : le ménage
     * local part d'abord, la révocation suit. Le cas « la requête échoue » laisse
     * la session vivante côté serveur, mais le navigateur, lui, est bien sorti.
     */
    void authApi.logout(refreshToken).catch(() => undefined);

    clearAuthStorage();
    setSsoCookie(null);
    queryClient.setQueryData(AUTH_ME_QUERY_KEY, null);
  }, [queryClient]);

  const hasProductAccess = useCallback(
    (slug: string) => normalizeProductAccess(user).some((p) => p.slug === slug),
    [user],
  );

  const getProductRole = useCallback(
    (slug: string) => normalizeProductAccess(user).find((p) => p.slug === slug)?.role ?? null,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, login, register, setSession, logout, hasProductAccess, getProductRole }),
    [getProductRole, hasProductAccess, isLoading, login, logout, register, setSession, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
